import { Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Cart, CustomerDetailsUpdateState, SocialLoginState } from '../../+state/stores.reducer';
import { ClientStore } from 'src/app/stores/stores';
import { take, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { getCurrentOrderMetaState, getSelectedStore, getTokens } from '../../+state/stores.selectors';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CheckoutService } from '../checkout.service';
import { AddCheckoutState, AddOrderMeta, CustomerDetailsUpdate } from '../../+state/stores.actions';
import { Country } from 'src/app/api/types/Country';
import { ReferenceDataService } from 'src/app/api/reference-data.service';
import { CustomerAuthService } from 'src/app/public/customer-auth.service';
import { CustomerDetailsUpdateRequest } from '../../types/CustomerSocialLogin';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-store-checkout-buzzer',
  templateUrl: './store-checkout-buzzer.component.html',
  styleUrl: './store-checkout-buzzer.component.scss'
})

export class StoreCheckoutBuzzerComponent implements OnInit, OnDestroy {
  selectedStore: ClientStore;
  unsubscribe$: Subject<void> = new Subject<void>();
  buzzerInfoForm: FormGroup;
  countriesList: Country[] = [];
  customerDetailsUpdateStatus = 'INITIAL';
  socialLoginState: SocialLoginState;
  customerDetailsUpdateState: CustomerDetailsUpdateState;
  customerDataUpdated = false;
  constructor(
    private store: Store<Cart>,
    private fb: FormBuilder,
    public checkoutService: CheckoutService,
    public auth: CustomerAuthService,
    private referenceDataService: ReferenceDataService
  ) {}

  ngOnInit(): void {
    this.store
      .pipe(select(getSelectedStore))
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((store) => {
        this.selectedStore = store;
        this.updateValidationRules();
      });

    this.store
      .select(getCurrentOrderMetaState)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((d) => {
        if (d) {
          this.updateOrderMetaDataValues();
        }
      });
  }

updateValidationRules() {
  let buzzerNumberValidators = [Validators.maxLength(50)];

  if (this.isRequired()) {
    buzzerNumberValidators.push(Validators.required);
  }
  
  this.buzzerInfoForm = this.fb.group({
    buzzerNumber: [
        this.checkoutService.getOrderMetaData('buzzerNumber'),
        { validators: buzzerNumberValidators, updateOn: 'change' },
      ],
  });

  this.buzzerInfoForm.updateValueAndValidity();
  this.checkFormValidity('Update validation rules');
  this.getControl('buzzerNumber').markAsDirty();
}

  checkFormValidity(description?: string) {
    this.store.dispatch(
      new AddCheckoutState('buzzerInfoFormValid', this.buzzerInfoForm.valid, {
        src: 'StoreCheckoutBuzzerComponent',
        description,
      })
    );
  }

addOrderMeta(metaKey: string, control: string) {
    this.store.dispatch(
      new AddOrderMeta(metaKey, this.getControl(control).value, {
        src: 'StoreCheckoutBuzzerComponent',
        description: `${metaKey} value changed`,
      })
    );
  }

  updateOrderMetaDataValues() {
    this.updateValues('buzzerNumber', 'buzzerNumber');
    this.checkFormValidity('Update order meta data values');
  }

  setCustomerDetails(response) {
    if (response.buzzerNumber && this.getControl('buzzerNumber') && !this.getControl('buzzerNumber').value) {
      this.getControl('buzzerNumber').patchValue(response.buzzerNumber, { emitEvent: true });
      this.addOrderMeta('buzzerNumber', 'buzzerNumber');
    }

    this.checkFormValidity();
  }

  customerDetailsChanged(): boolean {
    const state = this.customerDetailsUpdateState ?? this.socialLoginState;
    if (!state) {
      return false;
    }

    const fields = ['buzzerNumber'];

    const changed = fields.some((field) => (this.getControl(field)?.value || state[field]) && this.getControl(field)?.value != state[field]);

    if (changed) {
      this.customerDetailsUpdateStatus = 'INITIAL';
    }

    return changed;
  }

  updateCustomerDetails() {
  this.customerDataUpdated = true;
  this.store
    .select(getTokens)
    .pipe(take(1))
    .subscribe((tokens) => {
      const tokenPair = !!tokens.jwt ? tokens : null;
      const customerDetails: CustomerDetailsUpdateRequest = {
        tokens: tokenPair,
        buzzerNumber: this.getControl('buzzerNumber')?.value,
      };
      this.store.dispatch(new CustomerDetailsUpdate(customerDetails));
    });
}

  updateValues(control: string, meta: string) {
    if (this.getControl(control).value !== this.checkoutService.getOrderMetaData(meta)) {
      this.getControl(control).patchValue(this.checkoutService.getOrderMetaData(meta));
    }
  }

  isRequired(): boolean {
    return this.selectedStore?.settings?.ENABLE_BUZZER_NUMBER == 'MANDATORY';
  }

  getControl(name: string): AbstractControl {
    return this.buzzerInfoForm.get(name) || null;
  }

  showBuzzerInfo(): boolean {
    return ['MANDATORY', 'OPTIONAL'].includes(this.selectedStore?.settings?.ENABLE_BUZZER_NUMBER);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}


